/**
 * Every URL this console serves, and every URL it used to serve.
 *
 * One table, so a route and the redirect pointing at it cannot drift apart —
 * a test asserts every redirect target and every nav tab resolves to something
 * in ROUTES.
 *
 * A static path under `/tenants/` would also match `/tenants/:ref`, and the
 * failure is silent: the merchant page renders for a merchant called
 * "access" or "evidence". None is left; the old ones are redirects, and the
 * navigation crawl fails any page that still links to a redirect.
 */
export interface RouteDef {
  path: string;
  /** Which page component renders it — resolved in App.tsx. */
  page: string;
}

export const ROUTES: RouteDef[] = [
  { path: "/", page: "FleetPosture" },

  // --- Tenants ---
  { path: "/tenants", page: "Tenants" },
  { path: "/tenants/:ref", page: "Tenant" },
  { path: "/tenants/:ref/usage", page: "TenantUsage" },
  { path: "/tenants/:ref/history", page: "TenantHistory" },
  { path: "/tenants/:ref/users", page: "TenantUsers" },
  { path: "/tenants/:ref/users/:email", page: "TenantUser" },

  // --- Policy ---
  { path: "/policy/tiers", page: "Tiers" },
  { path: "/policy/access", page: "Access" },
  { path: "/policy/caps/:tier", page: "CapsTier" },
  // Labeeb's half of the same drill-in: GET and PATCH /admin/tiers/{tier_id}.
  { path: "/policy/tiers/:tier", page: "LabeebTier" },
  { path: "/policy/settings", page: "Flags" },

  // --- Governance ---
  { path: "/governance/permissions", page: "Permissions" },
  { path: "/governance/audit", page: "Audit" },
];

/**
 * Old URL → new URL. Nothing anyone bookmarked or pasted into a ticket breaks.
 *
 * `:param` in a target is filled from the matched route's params, so a
 * reference needing URL-encoding survives the hop. The previous
 * implementation read `window.location.pathname` and split on "/", which
 * mangled exactly those.
 *
 * Two generations are listed: the pre-`/mcp` routes (already redirects before
 * this change) and the `/mcp/*` ones this restructure replaces. The oldest
 * therefore takes two hops, which is fine — a redirect chain costs nothing and
 * collapsing it would mean remembering two mappings instead of one.
 */
export const REDIRECTS: { from: string; to: string }[] = [
  // Absorbed into the page that already summarised them. /policy/caps read
  // the same endpoint as the tier summary and wrote nothing; the EDITOR at
  // /policy/caps/:tier is untouched.
  // Blocking is fleet work — see the number, act on the list — so it moved
  // to Policy. Per-merchant blocking still lives on /tenants/{ref}.
  { from: "/tenants/access", to: "/policy/access" },
  // Beta grants was per-tenant work at fleet level: beta_agents exists only on
  // PATCH /admin/tenant/{id}, and the merchant record now edits it with the
  // rest of that merchant's context around it.
  { from: "/policy/beta", to: "/tenants" },
  // Two unbuilt Labeeb switches, now sections in Labeeb's Settings block.
  { from: "/policy/hitl", to: "/policy/settings" },
  { from: "/policy/welcome", to: "/policy/settings" },
  { from: "/policy/caps", to: "/policy/tiers" },
  // Same question as the flags beside it, so it became a block on that page.
  { from: "/policy/config", to: "/policy/settings" },
  // AI apps is a SECTION inside MCP's Settings block, not a tab of its own:
  // "which apps may connect" is a setting, and Policy's tab strip is for
  // what a change APPLIES TO, not for each knob. The URL keeps working so a
  // link already pasted into a thread still lands somewhere right.
  { from: "/policy/apps", to: "/policy/settings" },
  // No API behind it, and its two live cards repeated the Overview, so the
  // page was removed. A pasted link lands on the Overview.
  { from: "/tenants/evidence", to: "/" },
  // Pre-/mcp, kept from the previous restructure.
  { from: "/merchants", to: "/tenants" },
  { from: "/merchants/:ref", to: "/tenants/:ref" },

  // The /mcp/* generation.
  { from: "/mcp", to: "/" },
  { from: "/mcp/merchants", to: "/tenants" },
  { from: "/mcp/merchants/:ref", to: "/tenants/:ref" },
  { from: "/mcp/merchants/:ref/usage", to: "/tenants/:ref/usage" },
  { from: "/mcp/merchants/:ref/history", to: "/tenants/:ref/history" },
  { from: "/mcp/merchants/:ref/users", to: "/tenants/:ref/users" },
  { from: "/mcp/merchants/:ref/users/:email", to: "/tenants/:ref/users/:email" },
  { from: "/mcp/blocks", to: "/policy/access" },
  { from: "/mcp/quotas", to: "/policy/tiers" },
  { from: "/mcp/quotas/:tier", to: "/policy/caps/:tier" },
  { from: "/mcp/catalog", to: "/policy/tiers" },
  { from: "/mcp/settings", to: "/policy/settings" },
  { from: "/status", to: "/governance/permissions" },

  // Old URLs that still get pasted. Each lands on the tab that owns the work:
  // the catalog on Tiers & limits, settings on Settings, health and "your
  // access" on Permissions.
  { from: "/policy/catalog", to: "/policy/tiers" },
  { from: "/policy/flags", to: "/policy/settings" },
  { from: "/governance/settings", to: "/policy/settings" },
  { from: "/governance/health", to: "/governance/permissions" },
  { from: "/me", to: "/governance/permissions" },
];
